import { Capacitor } from "@capacitor/core";
import { getFormattedNumber, processAttachment, isImage } from "@msys/ui";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTolgee, useTranslate } from "@tolgee/react";
import { omit } from "lodash-es";
import React from "react";
import {
  AttachmentPhotoUploaderWithEditor,
  AttachmentPhotoUploaderWithEditorRef,
} from "../attachments/AttachmentPhotoUploaderWithEditor.js";
import { AttachmentUploader } from "../attachments/AttachmentUploader.js";
import { Attachment, getRotatedUrl } from "../attachments/helpers.js";
import { PropertyGalleryBox } from "./boxes/PropertyGalleryBox.js";
import { PropType } from "./properties.js";
import { usePropertyHelpers } from "./usePropertyHelpers.js";

export interface AllowedValueLabelWithOverrideProps {
  valueAsString: string;
  overriddenMedia: Attachment | null | undefined;
  onValueMediaChange: (
    valueAsString: string,
    media: Attachment | null | undefined
  ) => Promise<void>;
  propertyType: PropType;
  media: Attachment | null;
  value: string | number | boolean | null;
}

export function AllowedValueLabelWithOverride({
  overriddenMedia,
  valueAsString,
  onValueMediaChange,
  propertyType,
  media,
  value,
}: AllowedValueLabelWithOverrideProps) {
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const { getBoolLabel } = usePropertyHelpers();

  const attachmentInputRef = React.useRef<HTMLInputElement>(null);
  const attachmentPhotoUploaderRef =
    React.useRef<AttachmentPhotoUploaderWithEditorRef>(null);

  const language = useTolgee(["language"]).getLanguage()!;
  const { t } = useTranslate(["Global", "QuoteItem"]);

  return (
    <Stack direction="row" spacing={1} alignItems="center" flex={1}>
      {overriddenMedia !== undefined ? (
        overriddenMedia ? (
          <Badge color="secondary" variant="dot">
            <PropertyGalleryBox
              pictures={[overriddenMedia]
                .map(processAttachment)
                .filter(isImage)}
              flexGrow={0}
              flexShrink={0}
              width="80px"
              height="60px"
              imageHeight={60}
              aspectRatio="80 / 60"
              notInStack
              my={0.25}
              objectFit="contain"
              handleRotate={async (attachment, direction) => {
                await onValueMediaChange(valueAsString, {
                  ...overriddenMedia,
                  url: getRotatedUrl(attachment.url, direction),
                });
              }}
              handleDelete={async () => {
                await onValueMediaChange(valueAsString, null);
              }}
            />
          </Badge>
        ) : (
          <Badge color="secondary" variant="dot">
            <ImageNotSupportedIcon color="disabled" />
          </Badge>
        )
      ) : (
        media && (
          <PropertyGalleryBox
            pictures={[media].map(processAttachment).filter(isImage)}
            flexGrow={0}
            flexShrink={0}
            width="80px"
            height="60px"
            imageHeight={60}
            aspectRatio="80 / 60"
            notInStack
            my={0.25}
            objectFit="contain"
            handleRotate={async (attachment, direction) => {
              await onValueMediaChange(valueAsString, {
                ...omit(media, "__typename"),
                url: getRotatedUrl(attachment.url, direction),
              });
            }}
            handleDelete={async () => {
              await onValueMediaChange(valueAsString, null);
            }}
          />
        )
      )}
      <Box display="flex" flex={1}>
        <Typography>
          {value === null
            ? "–"
            : propertyType === "NUMBER"
              ? getFormattedNumber(value as number, language)
              : propertyType === "BOOLEAN"
                ? getBoolLabel(value as boolean)
                : (value as string)}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <>
          <IconButton
            size="small"
            color="secondary"
            disabled={isSubmitting}
            onClick={async () => {
              if (Capacitor.isNativePlatform()) {
                attachmentPhotoUploaderRef.current?.start();
              } else {
                attachmentInputRef.current?.click();
              }
            }}
          >
            {isSubmitting ? (
              <CircularProgress color="inherit" size={24} />
            ) : (
              <AddAPhotoIcon />
            )}
          </IconButton>
          {Capacitor.isNativePlatform() && (
            <AttachmentPhotoUploaderWithEditor
              ref={attachmentPhotoUploaderRef}
              onStart={() => {
                setIsSubmitting(true);
              }}
              onAttachment={async attachment => {
                if (attachment) {
                  await onValueMediaChange(valueAsString, attachment);
                }
                setIsSubmitting(false);
              }}
            />
          )}
          <AttachmentUploader
            innerRef={attachmentInputRef}
            accept={"image/*,.heic,.heif"}
            multiple={false}
            onStart={() => {
              setIsSubmitting(true);
            }}
            onComplete={async attachment => {
              if (attachment) {
                await onValueMediaChange(valueAsString, attachment);
              }
              setIsSubmitting(false);
            }}
          />
        </>
        {overriddenMedia !== undefined && (
          <IconButton
            color="primary"
            size="small"
            sx={{ flexGrow: 0, flexShrink: 0 }}
            onClick={async () => {
              await onValueMediaChange(valueAsString, undefined);
            }}
          >
            <Tooltip title={t("Reset", { ns: "Global" })}>
              <RestartAltIcon />
            </Tooltip>
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
}
